import {
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  useMediaQuery
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CustomToast from '../Common/CustomToast';
import { downloadInventoryData, getInventoryData, getStampDataById, updateInventory } from '../../Services/APIConfig';
import { ReactComponent as PreviewIcon } from '../../Assets/Images/passwordEye.svg';
import { ReactComponent as DisableIcon } from '../../Assets/Images/svgs/disable.svg';
import { nanoid } from 'nanoid';
import Loading from '../Common/LoaderComponent/Loader';
import { useNavigate, useParams } from 'react-router';

import InventoryTable from '../Common/InventoryTable';
import InventoryMenu from '../Common/InventoryMenu';
import arrowleft from '../../Assets/Images/arrow-left.svg';
import RenderMenu from '../Common/MenuDropdown/RenderMenu';
import DownloadIcon from '../../Assets/Images/svgs/download.svg';
import SaveIcon from '@mui/icons-material/Save';
import { downloadBase64File } from '../../utils/util';
import ViewDocumentPopUp from '../Common/ViewDocumentPopUp';
import InventoryDetailsMobile from '../Common/InventoryDetailsMobile';
import { downloadPDF } from '../../utils/dowloadPdf';
import PrimaryButton from '../Common/Button/PrimaryButton';
import { GREEN } from '../../Assets/Constants/theme';

const Inventory = () => {
  const [action, setAction] = useState('');
  const { stateId, denomination } = useParams();
  let itemMenu = [
    {
      title: 'xlsx',
      icon: SaveIcon,
      onClick: () => handleOptionSelect('xlsx'),
      fontSize: 'small'
    },
    {
      title: 'csv',
      icon: SaveIcon,
      onClick: () => handleOptionSelect('csv'),
      fontSize: 'small'
    }
  ];

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { showToast } = CustomToast();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 1020px)');
  const [loader, setLoader] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [openView, setOpenView] = useState(false);
  const [openRemarkDialog, setOpenRemarkDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortingModel] = useState({});

  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [remark, setRemark] = useState('');

  const [gridColumns, setGridColumns] = useState([]);
  const [gridRows, setGridRows] = useState([]);

  const generateMenuItems = status => {
    const commonMenu = [{ title: 'Preview', value: 'preview', icon: PreviewIcon }];
    switch (status) {
      case 'Available':
        return [
          ...commonMenu,
          { title: 'Mark as Blocked', value: 'blocked', icon: DisableIcon },
          { title: 'Mark as Reserved', value: 'reserved', icon: DisableIcon }
        ];

      case 'Blocked':
        return [...commonMenu, { title: 'Mark as Available', value: 'available', icon: DisableIcon }];
      case 'Used':
        return commonMenu;
      case 'Reserved':
        return [
          ...commonMenu,
          { title: 'Mark as Available', value: 'available', icon: DisableIcon },
          { title: 'Mark as Used', value: 'used', icon: DisableIcon }
        ];
      default:
        return commonMenu;
    }
  };

  const fetchData = async (payload = {}) => {
    setLoader(true);

    try {
      const { status, columns, data, message, error, totalCount } = await getInventoryData(stateId, {
        page: pageCount,
        limit: pageSize,
        stampDenomination: denomination,
        ...payload
      });

      if (status === 200) {
        setTotalCount(totalCount);
        const tempColumns = columns.map(item => ({
          ...item,
          flex: 1,
          sortable: item?.sorting || false,
          filterable: false,
          disableColumnMenu: item?.sorting ? false : true
        }));

        tempColumns.push({
          field: 'more',
          headerName: '',
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: params => {
            const tempMenu = generateMenuItems(params.row.status);
            return (
              <InventoryMenu
                handleClick={val => {
                  if (val === 'preview') {
                    setSelectedRow(params.row);
                    fetchStampDetails(params.row.stampInventoryId);
                  } else {
                    setSelectedRow(params.row);
                    handleUpdateClick(val);
                  }
                }}
                handleClose={() => {}}
                kebabMenuItem={tempMenu}
              />
            );
          }
        });

        setGridColumns(tempColumns);
        setGridRows(data.map(item => ({ ...item, id: nanoid() })));
      } else {
        showToast('error', message || error);
      }
    } catch (err) {
      showToast('error', err?.message || err?.error || 'An error occurred');
    } finally {
      setInitialLoading(false);
      setLoader(false);
    }
  };

  const fetchStampDetails = async stampInventoryId => {
    setLoader(true);
    try {
      const res = await getStampDataById(stampInventoryId);
      if (res.status === 200) {
        setSelectedRow({ ...res.data, stampInventoryId });
        setOpenView(true);
      } else {
        showToast('error', res?.message || res?.error);
      }
    } catch (error) {
      showToast('error', error?.message || error?.error);
    } finally {
      setInitialLoading(false);
      setLoader(false);
    }
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleOpenConfirmation = () => {
    setOpenConfirmation(true);
  };

  const handleUpdateClick = async action => {
    setAction(action);
    setOpenRemarkDialog(true);
  };

  const handleDownloadModal = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleOptionSelect = async val => {
    const payload = { stateId, stampDenomination: denomination, type: val, ...sortModel };
    try {
      const response = await downloadInventoryData(payload);
      if (response.status === 200) {
        downloadBase64File(
          response?.base64Data || response.data,
          `${gridRows[0]?.stateName + '-' + `Rs.${denomination}` + '-inventory-details'}` + `.${val}`,
          val,
          'application'
        );

        showToast('success', response?.message || '');
      } else {
        showToast('error', response?.message || response?.error);
      }
    } catch (error) {
      showToast('error', error?.message || error?.error);
    }
  };

  const handleConfirmUpdate = async () => {
    setLoader(true);
    try {
      const res = await updateInventory(action, selectedRow.stampInventoryId, {
        remark
      });
      if (res.status === 200) {
        showToast('success', res.message || '');
        if (action === 'used') {
          downloadPDF('data:application/pdf;base64,' + res.base64Data, selectedRow.stampCertificateId || '');
          setOpenView(false);
        }
        fetchData();
        setOpenRemarkDialog(false);
        setRemark('');
      } else {
        showToast('error', res?.message || res?.error);
      }
    } catch (error) {
      showToast('error', error?.message || error?.error);
    } finally {
      setLoader(false);
    }
  };

  const handleClose = () => {
    setOpenView(false);
    setSelectedRow(null);
  };

  const handleRemarkDialogClose = () => {
    setOpenRemarkDialog(false);
    setRemark('');
  };

  useEffect(() => {
    fetchData();
  }, [pageCount, pageSize]);

  return (
    <>
      {initialLoading ? (
        <Loading open={true} />
      ) : (
        <>
          {!isMobile && openView && selectedRow && (
            <ViewDocumentPopUp
              open={openView}
              handleClose={handleClose}
              data={selectedRow}
              docName={''}
              DocUrl={selectedRow?.stampPaperPdf}
              isPdf={true}
              inventoryPage
              handleUpdateClick={handleUpdateClick}
              handleCloseConfirmation={handleCloseConfirmation}
              handleOpenConfirmation={handleOpenConfirmation}
              openConfirmation={openConfirmation}
            />
          )}

          {isMobile && openView && selectedRow && (
            <InventoryDetailsMobile
              open={openView}
              handleClose={handleClose}
              data={selectedRow}
              docName={selectedRow?.stampCertificateId || ''}
              DocUrl={selectedRow?.stampPaperPdf}
              isPdf={true}
              inventoryPage
              handleUpdateClick={handleUpdateClick}
              handleCloseConfirmation={handleCloseConfirmation}
              handleOpenConfirmation={handleOpenConfirmation}
              openConfirmation={openConfirmation}
            />
          )}

          <Loading open={loader} />
          <Box width='99%'>
            <Stack sx={{ marginTop: '40px', height: '100%' }}>
              <Box display='flex' alignItems='center' justifyContent='space-between'>
                <Box display='flex' alignItems='center' gap='10px' ml='20px' mb='20px'>
                  <img
                    src={arrowleft}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigate('/dashboard', { state: { inventory: true } });
                    }}
                  />
                  <Typography>Back</Typography>
                </Box>
                <>
                  <Box display='flex' gap='10px'>
                    <PrimaryButton
                      className={'semiBold14'}
                      value={'Bulk Inventory'}
                      backgroundColor={GREEN}
                      borderRadius={'8px'}
                      fontWeight={400}
                      padding='10px 16px'
                      textcolor={'#000'}
                      onClick={() => {
                        navigate(`/bulk-inventory/${stateId}/${denomination}`);
                      }}
                    />
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                        <img
                          src={DownloadIcon}
                          alt='Download Icon'
                          style={{ cursor: 'pointer', width: '50px', height: '40px', marginRight: '25px' }}
                          onClick={handleDownloadModal}
                        />
                      </div>
                      <RenderMenu
                        anchorEl={anchorEl}
                        open={open}
                        handleClose={() => {
                          setAnchorEl(null);
                        }}
                        itemMenu={itemMenu}
                      />
                    </div>
                  </Box>
                </>
              </Box>

              <Grid container>
                <Grid xs={12} item sx={{ width: '100%', overflowX: 'scroll' }}>
                  <Grid xs={12} item sx={{ minWidth: '1000px' }}>
                    <InventoryTable
                      rows={gridRows}
                      columns={gridColumns}
                      totalRowCount={totalCount}
                      hasDuplicates={false}
                      rowsPerPageRender={true}
                      pageCount={pageCount}
                      pageSize={pageSize}
                      updatePageCount={(count, size) => {
                        setPageSize(size);
                        setPageCount(count);
                      }}
                      handleSortModelChange={sort => {
                        const temp = {
                          sortingColumnName: sort?.[0]?.field || '',
                          sortingType: sort?.[0]?.sort || ''
                        };
                        if (temp.sortingColumnName === '' && temp.sortingType === '') {
                          delete temp.sortingColumnName;
                          delete temp.sortingType;
                        }
                        setSortingModel(temp);
                        fetchData(temp);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Dialog open={openRemarkDialog} onClose={handleRemarkDialogClose}>
                <DialogTitle>Add Remark</DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    multiline
                    rows={4}
                    variant='outlined'
                    fullWidth
                    value={remark}
                    onChange={e => setRemark(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant='body2' color='textSecondary'>
                    Please provide your remarks before updating the status.
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Box display='flex' alignItems='center' gap='10px' width='100%'>
                    <Button
                      variant='contained'
                      id='gen-modal-btn'
                      onClick={handleRemarkDialogClose}
                      sx={{
                        height: '20px',
                        flex: 1,
                        padding: '17px 20px',
                        borderRadius: '8px',
                        backgroundColor: 'transparent',
                        border: '2px solid #b6d423',
                        color: '#b6d423',
                        boxShadow: 'none',
                        marginTop: '20px',
                        fontSize: '16px',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      <div className='medium5'>Cancel</div>
                    </Button>

                    <Button
                      variant='contained'
                      id='gen-modal-btn'
                      onClick={handleConfirmUpdate}
                      disabled={!remark}
                      sx={{
                        height: '40px',
                        flex: 1,
                        width: 'auto',
                        padding: '17px 20px',
                        borderRadius: '8px',
                        backgroundColor: '#b6d423',
                        color: '#000',
                        boxShadow: 'none',
                        marginTop: '20px',
                        fontSize: '16px',
                        '&:hover': {
                          backgroundColor: '#b6d423',
                          boxShadow: 'none'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#b6d423',
                          opacity: '0.7'
                        },
                        '&.right-corner': {
                          position: 'absolute',
                          bottom: '20px',
                          right: '20px'
                        }
                      }}
                      s
                    >
                      <div className='medium5'>Update</div>
                    </Button>
                  </Box>
                </DialogActions>
              </Dialog>
            </Stack>
          </Box>
        </>
      )}
    </>
  );
};

export default Inventory;
